import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Accordion, AccordionSummary, AccordionDetails,
    Chip, Divider, Paper, Grid, IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FlightIcon from '@mui/icons-material/Flight';
import HotelIcon from '@mui/icons-material/Hotel';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import EmailIcon from '@mui/icons-material/Email';
import MapIcon from '@mui/icons-material/Map';
import GroupIcon from '@mui/icons-material/Group';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import MenuIcon from '@mui/icons-material/Menu';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import TuneIcon from '@mui/icons-material/Tune';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PushPinIcon from '@mui/icons-material/PushPin';
import NoteIcon from '@mui/icons-material/Note';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ArchiveIcon from '@mui/icons-material/Archive';
import StarIcon from '@mui/icons-material/Star';
import CloseIcon from '@mui/icons-material/Close';
import { BASE_PATH } from './config';

const DARK_BG      = '#1a1d2e';
const DARK_SURFACE = '#242740';
const DARK_CARD    = '#1e2235';
const DARK_BORDER  = 'rgba(255,255,255,0.10)';
const ACCENT       = '#6C63FF';
const ACCENT2      = '#a78bfa';
const TEXT_PRIMARY = '#f0f0f5';
const TEXT_MUTED   = 'rgba(240,240,245,0.55)';
const GREEN        = '#4caf50';
const AMBER        = '#ffc107';
const RED          = '#f44336';
const TEAL         = '#26c6da';

/* ── Small reusable pieces ─────────────────────────────────────── */

const SectionIcon = ({ icon: Icon, color = ACCENT, size = 32 }) => (
    <Box sx={{
        width: size + 16, height: size + 16, borderRadius: '14px',
        bgcolor: `${color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: `1px solid ${color}44`, flexShrink: 0,
    }}>
        <Icon sx={{ fontSize: size, color }} />
    </Box>
);

const StepBadge = ({ n }) => (
    <Box sx={{
        width: 28, height: 28, borderRadius: '50%', bgcolor: ACCENT,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, fontWeight: 700, fontSize: 13, color: '#fff',
    }}>{n}</Box>
);

const KeyboardKey = ({ children }) => (
    <Box component="span" sx={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        px: 1, py: 0.25, borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600,
        bgcolor: DARK_BORDER, border: `1px solid rgba(255,255,255,0.25)`,
        color: TEXT_PRIMARY, mx: 0.25,
    }}>{children}</Box>
);

const TipBox = ({ children }) => (
    <Box sx={{
        display: 'flex', gap: 1.5, alignItems: 'flex-start',
        bgcolor: `${ACCENT}11`, border: `1px solid ${ACCENT}33`,
        borderRadius: 2, p: 1.5, mt: 1.5,
    }}>
        <LightbulbIcon sx={{ color: AMBER, mt: 0.2, flexShrink: 0, fontSize: 18 }} />
        <Typography variant="body2" style={{ color: TEXT_MUTED }}>{children}</Typography>
    </Box>
);

const IconRow = ({ icon: Icon, color, label, desc }) => (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 1.5 }}>
        <Box sx={{
            width: 34, height: 34, borderRadius: 2, bgcolor: `${color}22`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `1px solid ${color}44`, flexShrink: 0,
        }}>
            <Icon sx={{ fontSize: 18, color }} />
        </Box>
        <Box>
            <Typography variant="body2" fontWeight={600} style={{ color: TEXT_PRIMARY }}>{label}</Typography>
            <Typography variant="caption" style={{ color: TEXT_MUTED }}>{desc}</Typography>
        </Box>
    </Box>
);

const HelpSection = ({ icon, iconColor, title, badge, defaultExpanded = false, children }) => (
    <Accordion
        defaultExpanded={defaultExpanded}
        disableGutters
        sx={{
            bgcolor: DARK_SURFACE,
            border: `1px solid ${DARK_BORDER}`,
            borderRadius: '12px !important',
            mb: 1.5,
            '&:before': { display: 'none' },
            '&.Mui-expanded': { borderColor: `${iconColor || ACCENT}55` },
        }}
    >
        <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: TEXT_MUTED }} />}
            sx={{ px: 2.5, py: 1 }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SectionIcon icon={icon} color={iconColor || ACCENT} />
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700} style={{ color: TEXT_PRIMARY }}>
                            {title}
                        </Typography>
                        {badge && (
                            <Chip label={badge} size="small"
                                sx={{ fontSize: '0.65rem', height: 18, bgcolor: `${ACCENT}33`, color: ACCENT2, border: `1px solid ${ACCENT}55` }} />
                        )}
                    </Box>
                </Box>
            </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
            <Divider sx={{ borderColor: DARK_BORDER, mb: 2 }} />
            {children}
        </AccordionDetails>
    </Accordion>
);

/* ── Main component ─────────────────────────────────────────────── */

const HelpPage = () => {
    const navigate = useNavigate();
    return (
        <Box sx={{ minHeight: '100vh', bgcolor: DARK_BG, color: TEXT_PRIMARY }}>

            {/* ── Hero header ─────────────────────────────────────── */}
            <Box sx={{
                background: `linear-gradient(135deg, ${DARK_SURFACE} 0%, #16193a 100%)`,
                borderBottom: `1px solid ${DARK_BORDER}`,
                px: { xs: 2, sm: 4 }, py: { xs: 4, sm: 6 },
                textAlign: 'center',
                position: 'relative',
            }}>
                {/* Close button */}
                <IconButton
                    onClick={() => navigate(-1)}
                    aria-label="Close help"
                    sx={{
                        position: 'absolute', top: 16, right: 16,
                        color: TEXT_MUTED,
                        bgcolor: DARK_CARD,
                        border: `1px solid ${DARK_BORDER}`,
                        '&:hover': { bgcolor: `${ACCENT}22`, color: ACCENT2, borderColor: ACCENT },
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <Box sx={{
                        width: 72, height: 72, borderRadius: '20px',
                        background: `linear-gradient(135deg, ${ACCENT}, #a78bfa)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: `0 8px 32px ${ACCENT}55`,
                    }}>
                        <MapIcon sx={{ fontSize: 40, color: '#fff' }} />
                    </Box>
                </Box>
                <Typography variant="h4" fontWeight={800} gutterBottom style={{ color: TEXT_PRIMARY }}>
                    Help & Documentation
                </Typography>
                <Typography variant="body1" style={{ color: TEXT_MUTED, maxWidth: 540, margin: '0 auto' }}>
                    Everything you need to know about Agent Trip Planner -- from creating your first trip to forwarding booking emails.
                </Typography>

                {/* Quick nav chips */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 3 }}>
                    {[
                        ['Trips', '#trips'], ['Activities', '#activities'], ['Email Import', '#email'],
                        ['AI Assistant', '#ai'], ['Expenses', '#expenses'], ['Sharing', '#sharing'],
                        ['Navigation', '#navbar'],
                    ].map(([label, href]) => (
                        <Chip
                            key={label}
                            label={label}
                            component="a"
                            href={href}
                            clickable
                            size="small"
                            sx={{
                                bgcolor: DARK_CARD, color: TEXT_PRIMARY, border: `1px solid ${DARK_BORDER}`,
                                '&:hover': { bgcolor: `${ACCENT}22`, borderColor: ACCENT, color: ACCENT2 },
                                cursor: 'pointer',
                            }}
                        />
                    ))}
                </Box>
            </Box>

            {/* ── Content ─────────────────────────────────────────── */}
            <Box sx={{ maxWidth: 820, mx: 'auto', px: { xs: 2, sm: 3 }, py: 4 }}>

                {/* ── 1. Navigation bar ─────────────────────────────── */}
                <Typography variant="overline" id="navbar" sx={{ color: ACCENT, letterSpacing: 2, mb: 1, display: 'block' }}>
                    Interface
                </Typography>

                <HelpSection icon={MenuIcon} iconColor={TEAL} title="Navigation Bar & Menu" defaultExpanded>
                    <Typography variant="body2" style={{ color: TEXT_MUTED }} gutterBottom>
                        The top bar is always visible and gives you quick access to the most important functions.
                    </Typography>

                    <Box sx={{ bgcolor: DARK_CARD, borderRadius: 2, p: 2, mb: 2, border: `1px solid ${DARK_BORDER}` }}>
                        {/* Illustration: top bar */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', opacity: 0.85 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <MenuIcon sx={{ color: TEXT_MUTED, fontSize: 20 }} />
                                <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>A</Typography>
                                </Box>
                            </Box>
                            <Typography variant="body2" fontWeight={700} style={{ color: ACCENT }}>Agent Trip Planner</Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <HomeIcon sx={{ color: TEXT_MUTED, fontSize: 20 }} />
                                <SmartToyIcon sx={{ color: ACCENT, fontSize: 20 }} />
                                <NotificationsIcon sx={{ color: TEXT_MUTED, fontSize: 20 }} />
                                <TuneIcon sx={{ color: TEXT_MUTED, fontSize: 20 }} />
                            </Box>
                        </Box>
                    </Box>

                    <IconRow icon={MenuIcon} color={TEAL} label="☰ Hamburger Menu (top-left)"
                        desc="Opens the side navigation panel with links to all main sections of the app." />
                    <IconRow icon={HomeIcon} color={GREEN} label="🏠 Home"
                        desc="Returns to your trip list where all your active trips are shown." />
                    <IconRow icon={SmartToyIcon} color={ACCENT} label="🤖 AI Assistant"
                        desc="Opens the AI chat. Ask it to add activities, suggest places, or build your itinerary automatically." />
                    <IconRow icon={NotificationsIcon} color={AMBER} label="📬 Message Center (bell icon)"
                        desc="Shows notifications from email imports, trip share invitations, and system updates. The badge number = unread messages." />
                    <IconRow icon={TuneIcon} color={ACCENT2} label="⚙ Settings"
                        desc="Access language settings, account preferences, and other configuration options." />

                    <Divider sx={{ borderColor: DARK_BORDER, my: 2 }} />
                    <Typography variant="subtitle2" fontWeight={700} style={{ color: TEXT_PRIMARY }} gutterBottom>
                        Side Menu links
                    </Typography>
                    {[
                        ['👤 Profile Settings', 'Update your name, WhatsApp number, email, and password.'],
                        ['📸 Memories', 'View and upload photos for the trip you are currently viewing.'],
                        ['💵 Expenses', 'Access all your expense reports across trips.'],
                        ['🗂 Archived Trips', 'See trips you have completed or manually archived.'],
                        ['🤝 Sharing', 'Manage trips you have shared with other users.'],
                        ['✈ Flight Search', 'Search for live flight options and prices.'],
                        ['❓ Help', 'This page  --  detailed documentation.'],
                    ].map(([label, desc]) => (
                        <Box key={label} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <ArrowForwardIcon sx={{ fontSize: 14, color: ACCENT, mt: 0.5, flexShrink: 0 }} />
                            <Typography variant="body2" style={{ color: TEXT_MUTED }}>
                                <strong style={{ color: TEXT_PRIMARY }}>{label}</strong> -- {desc}
                            </Typography>
                        </Box>
                    ))}
                </HelpSection>

                {/* ── 2. Trips ──────────────────────────────────────── */}
                <Typography variant="overline" id="trips" sx={{ color: ACCENT, letterSpacing: 2, mb: 1, display: 'block', mt: 3 }}>
                    Trips
                </Typography>

                <HelpSection icon={CalendarMonthIcon} iconColor={GREEN} title="Creating & Managing Trips">
                    <Typography variant="body2" style={{ color: TEXT_MUTED }} gutterBottom>
                        A <strong style={{ color: TEXT_PRIMARY }}>Trip</strong> is the top-level container. It has a destination, start/end dates, and holds all your daily activities.
                    </Typography>

                    <Typography variant="subtitle2" fontWeight={700} style={{ color: TEXT_PRIMARY }} sx={{ mt: 2, mb: 1 }}>
                        How to create a trip
                    </Typography>
                    {[
                        'On the home screen tap the + New Trip button.',
                        'Enter the destination, trip name, and travel dates.',
                        'Optionally set a budget and number of travelers.',
                        'Tap Create \u2014 you\'ll land on the trip itinerary immediately.',
                    ].map((step, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 1 }}>
                            <StepBadge n={i + 1} />
                            <Typography variant="body2" style={{ color: TEXT_MUTED }}>{step}</Typography>
                        </Box>
                    ))}

                    <Divider sx={{ borderColor: DARK_BORDER, my: 2 }} />
                    <Typography variant="subtitle2" fontWeight={700} style={{ color: TEXT_PRIMARY }} gutterBottom>
                        Trip cards on the home screen
                    </Typography>
                    {[
                        ['✏ Edit icon', 'Opens the trip editor  --  change name, dates, destination, or budget.'],
                        ['🗑 Delete icon', 'Permanently deletes the trip and all its activities.'],
                        ['📦 Archive', 'Moves completed trips to Archived Trips; hidden from the main list.'],
                        ['👁 View Itinerary', 'Opens the full day-by-day itinerary for that trip.'],
                    ].map(([label, desc]) => (
                        <Box key={label} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <ArrowForwardIcon sx={{ fontSize: 14, color: ACCENT, mt: 0.5, flexShrink: 0 }} />
                            <Typography variant="body2" style={{ color: TEXT_MUTED }}>
                                <strong style={{ color: TEXT_PRIMARY }}>{label}</strong> -- {desc}
                            </Typography>
                        </Box>
                    ))}
                    <TipBox>
                        The AI Trip Wizard can auto-generate a full itinerary for you. Tap the wand/sparkle icon on the home screen and describe your trip in plain language.
                    </TipBox>
                </HelpSection>

                {/* ── 3. Activities ─────────────────────────────────── */}
                <Typography variant="overline" id="activities" sx={{ color: ACCENT, letterSpacing: 2, mb: 1, display: 'block', mt: 3 }}>
                    Activities
                </Typography>

                <HelpSection icon={LocalActivityIcon} iconColor={AMBER} title="Activity Types & Fields">
                    <Typography variant="body2" style={{ color: TEXT_MUTED }} gutterBottom>
                        Every item on your itinerary is an <strong style={{ color: TEXT_PRIMARY }}>Activity</strong> -- one of four types:
                    </Typography>

                    <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                        {[
                            { icon: FlightIcon, color: '#29b6f6', label: 'Flight / Transportation', desc: 'Flights, trains, buses, car rentals, ferries. Shows route, flight number, seat, and carrier.' },
                            { icon: HotelIcon, color: '#ab47bc', label: 'Lodging', desc: 'Hotels, Airbnb, hostels. Shows check-in/out dates, address, confirmation number.' },
                            { icon: LocalActivityIcon, color: AMBER, label: 'Activity', desc: 'Tours, restaurants, attractions, excursions. Shows duration and location.' },
                            { icon: DirectionsBusIcon, color: GREEN, label: 'General Transport', desc: 'Taxi, shuttle, walk. A lighter transport type without flight-specific fields.' },
                        ].map(({ icon: Icon, color, label, desc }) => (
                            <Grid item xs={12} sm={6} key={label}>
                                <Paper sx={{ bgcolor: DARK_CARD, border: `1px solid ${color}44`, borderRadius: 2, p: 1.5, height: '100%' }}>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                                        <Icon sx={{ color, fontSize: 20 }} />
                                        <Typography variant="body2" fontWeight={700} style={{ color: TEXT_PRIMARY }}>{label}</Typography>
                                    </Box>
                                    <Typography variant="caption" style={{ color: TEXT_MUTED }}>{desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </HelpSection>

                <HelpSection icon={AddCircleOutlineIcon} iconColor={GREEN} title="Adding Activities Manually">
                    {[
                        'Open the trip itinerary and scroll to the day you want.',
                        'Tap the + Add Activity button at the bottom of that day.',
                        'Choose the activity type (Flight, Lodging, Transport, Activity).',
                        'Fill in the required fields  --  title, time, and any extras.',
                        'Tap Save.',
                    ].map((step, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 1 }}>
                            <StepBadge n={i + 1} />
                            <Typography variant="body2" style={{ color: TEXT_MUTED }}>{step}</Typography>
                        </Box>
                    ))}

                    <Divider sx={{ borderColor: DARK_BORDER, my: 2 }} />
                    <Typography variant="subtitle2" fontWeight={700} style={{ color: TEXT_PRIMARY }} gutterBottom>
                        Activity row buttons
                    </Typography>
                    <IconRow icon={CheckCircleOutlineIcon} color={GREEN} label="✓ Approve (green button)"
                        desc="Confirms the activity on your itinerary. Marks it as final." />
                    <IconRow icon={DeleteOutlineIcon} color={RED} label="✕ Remove (red button)"
                        desc="Permanently deletes the activity from this day." />
                    <IconRow icon={EditIcon} color={ACCENT} label="✏ Edit (pencil icon)"
                        desc="Opens the edit form to change any field of the activity." />
                    <IconRow icon={PushPinIcon} color={AMBER} label="📌 Pin"
                        desc="Pins the activity so it always appears at the top of the day's list. Useful for check-ins and flight departures." />
                    <IconRow icon={DragIndicatorIcon} color={TEXT_MUTED} label="⠿ Drag handle"
                        desc="Drag activities up or down to reorder them within the same day." />
                    <TipBox>
                        Activities added via email import appear with a purple  badge and require approval before they are fully confirmed on the itinerary.
                    </TipBox>
                </HelpSection>

                {/* ── 4. Email import ───────────────────────────────── */}
                <Typography variant="overline" id="email" sx={{ color: ACCENT, letterSpacing: 2, mb: 1, display: 'block', mt: 3 }}>
                    Email Import
                </Typography>

                <HelpSection icon={EmailIcon} iconColor={TEAL} title="Importing Bookings by Email" badge="Auto">
                    <Typography variant="body2" style={{ color: TEXT_MUTED }} gutterBottom>
                        Forward any booking confirmation email to <strong style={{ color: TEAL }}>info@agentriplanner.com</strong> from the same email address you registered with.
                        The AI will extract all travel details and add them as pending activities on the matching trip.
                    </Typography>

                    <Box sx={{ bgcolor: DARK_CARD, border: `1px solid ${TEAL}44`, borderRadius: 2, p: 2, my: 2 }}>
                        <Typography variant="caption" fontWeight={700} style={{ color: TEAL }}>📧 FORWARDING ADDRESS</Typography>
                        <Typography variant="h6" fontWeight={800} style={{ color: TEXT_PRIMARY, letterSpacing: 0.5 }}>
                            info@agentriplanner.com
                        </Typography>
                        <Typography variant="caption" style={{ color: TEXT_MUTED }}>
                            Send from the email linked to your account
                        </Typography>
                    </Box>

                    <Typography variant="subtitle2" fontWeight={700} style={{ color: TEXT_PRIMARY }} gutterBottom>
                        What gets extracted automatically
                    </Typography>
                    {[
                        ['✈ Flights', 'All legs including connections  --  carrier, flight number, seats, times, IATA codes.'],
                        ['🏨 Hotels / Airbnb', 'Check-in & check-out dates, address, booking reference.'],
                        ['🚌 Transport', 'Trains, buses, car hire confirmations.'],
                        ['🎟 Activities', 'Tour confirmations, restaurant reservations with date/time.'],
                    ].map(([label, desc]) => (
                        <Box key={label} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <ArrowForwardIcon sx={{ fontSize: 14, color: TEAL, mt: 0.5, flexShrink: 0 }} />
                            <Typography variant="body2" style={{ color: TEXT_MUTED }}>
                                <strong style={{ color: TEXT_PRIMARY }}>{label}</strong> -- {desc}
                            </Typography>
                        </Box>
                    ))}

                    <Typography variant="subtitle2" fontWeight={700} style={{ color: TEXT_PRIMARY }} sx={{ mt: 2 }} gutterBottom>
                        After the email is processed
                    </Typography>
                    {[
                        'A notification appears in your Message Center (bell icon).',
                        'New activities show up on the correct trip days with a pending badge.',
                        'Review each activity and tap ✓ Approve or ✕ Remove.',
                        'The email body is saved  --  tap "Show email" in the Message Center to re-read it.',
                    ].map((step, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 1 }}>
                            <StepBadge n={i + 1} />
                            <Typography variant="body2" style={{ color: TEXT_MUTED }}>{step}</Typography>
                        </Box>
                    ))}

                    <TipBox>
                        The system matches activities to a trip by comparing the booking dates with your trip's start/end dates. Make sure your trip dates are set correctly before forwarding emails.
                    </TipBox>
                </HelpSection>

                {/* ── 5. AI Assistant ───────────────────────────────── */}
                <Typography variant="overline" id="ai" sx={{ color: ACCENT, letterSpacing: 2, mb: 1, display: 'block', mt: 3 }}>
                    AI Features
                </Typography>

                <HelpSection icon={SmartToyIcon} iconColor={ACCENT} title="AI Assistant" badge="Powered by Gemini">
                    <Typography variant="body2" style={{ color: TEXT_MUTED }} gutterBottom>
                        The AI Assistant understands natural language. Tap the robot icon in the top bar to open the chat.
                    </Typography>

                    <Typography variant="subtitle2" fontWeight={700} style={{ color: TEXT_PRIMARY }} sx={{ mt: 1.5, mb: 1 }} gutterBottom>
                        Example prompts you can use
                    </Typography>
                    {[
                        '"Add a 3-hour walking tour of the old city on day 2"',
                        '"Suggest dinner restaurants near my hotel on Thursday"',
                        '"Move the museum visit to day 4 at 10am"',
                        '"Build a full 5-day itinerary for Rome with culture and food"',
                        '"What\'s the best time to visit the Colosseum?"',
                    ].map((prompt) => (
                        <Box key={prompt} sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
                            <StarIcon sx={{ fontSize: 14, color: AMBER, mt: 0.4, flexShrink: 0 }} />
                            <Typography variant="body2" style={{ color: TEXT_MUTED, fontStyle: 'italic' }}>{prompt}</Typography>
                        </Box>
                    ))}

                    <Divider sx={{ borderColor: DARK_BORDER, my: 2 }} />
                    <Typography variant="subtitle2" fontWeight={700} style={{ color: TEXT_PRIMARY }} gutterBottom>
                        AI-generated activities
                    </Typography>
                    <Typography variant="body2" style={{ color: TEXT_MUTED }}>
                        Activities suggested by the AI are added with a <Chip label="AI" size="small" sx={{ fontSize: '0.6rem', height: 16, bgcolor: `${ACCENT}33`, color: ACCENT2 }} /> badge.
                        They are pending confirmation -- approve or dismiss each one from the itinerary view.
                    </Typography>
                    <TipBox>
                        The AI has context of your current trip -- it knows the destination, dates, and existing activities. The more you use it within a trip, the better the suggestions.
                    </TipBox>
                </HelpSection>

                {/* ── 6. Itinerary view ─────────────────────────────── */}
                <HelpSection icon={MapIcon} iconColor={ACCENT2} title="Trip Itinerary View">
                    <Typography variant="body2" style={{ color: TEXT_MUTED }} gutterBottom>
                        The Itinerary view is the heart of the app. Each trip day is a card with a chronological list of activities.
                    </Typography>

                    <Typography variant="subtitle2" fontWeight={700} style={{ color: TEXT_PRIMARY }} sx={{ mt: 1.5, mb: 1 }}>
                        Day card controls
                    </Typography>
                    <IconRow icon={AddCircleOutlineIcon} color={GREEN} label="+ Add Activity"
                        desc="Opens the form to manually add a new activity to that specific day." />
                    <IconRow icon={SmartToyIcon} color={ACCENT} label="AI suggest for this day"
                        desc="Asks the AI to propose activities specifically for that day's location and time." />
                    <IconRow icon={NoteIcon} color={AMBER} label="Day notes"
                        desc="Add a free-text note visible at the top of the day -- useful for reminders, weather info, etc." />

                    <Divider sx={{ borderColor: DARK_BORDER, my: 2 }} />
                    <Typography variant="subtitle2" fontWeight={700} style={{ color: TEXT_PRIMARY }} gutterBottom>
                        Activity badges
                    </Typography>
                    {[
                        ['Pending', `${AMBER}`, 'Activity was added by email import or AI and needs your approval.'],
                        ['AI', `${ACCENT}`, 'Activity was suggested by the AI assistant.'],
                        ['Email', `${TEAL}`, 'Activity was extracted from a forwarded booking email.'],
                        ['Pinned', `${AMBER}`, 'Activity is pinned to the top of the day.'],
                    ].map(([label, color, desc]) => (
                        <Box key={label} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 1 }}>
                            <Chip label={label} size="small" sx={{ fontSize: '0.65rem', height: 18, bgcolor: `${color}33`, color, border: `1px solid ${color}55`, flexShrink: 0 }} />
                            <Typography variant="caption" style={{ color: TEXT_MUTED }}>{desc}</Typography>
                        </Box>
                    ))}
                </HelpSection>

                {/* ── 7. Expenses ───────────────────────────────────── */}
                <Typography variant="overline" id="expenses" sx={{ color: ACCENT, letterSpacing: 2, mb: 1, display: 'block', mt: 3 }}>
                    Expenses
                </Typography>

                <HelpSection icon={AttachMoneyIcon} iconColor={GREEN} title="Expense Tracking">
                    <Typography variant="body2" style={{ color: TEXT_MUTED }} gutterBottom>
                        Track spending per trip, split costs among travelers, and view analytics across all trips.
                    </Typography>

                    <Typography variant="subtitle2" fontWeight={700} style={{ color: TEXT_PRIMARY }} sx={{ mt: 1.5, mb: 1 }}>
                        Key features
                    </Typography>
                    {[
                        ['Per-trip expenses', 'Add expenses directly from the trip page. Each expense has a category, amount, currency, and payer.'],
                        ['Expense splitting', 'Divide costs equally or by custom amounts among trip participants.'],
                        ['Settlement tracker', 'See who owes what based on split expenses  --  simplifies group travel accounting.'],
                        ['Expense reports', 'Access aggregated reports from the Expenses link in the side menu.'],
                        ['Analytics', 'Visual breakdowns by category and day -- find where you\'re over-spending easily.'],
                    ].map(([label, desc]) => (
                        <Box key={label} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <ArrowForwardIcon sx={{ fontSize: 14, color: GREEN, mt: 0.5, flexShrink: 0 }} />
                            <Typography variant="body2" style={{ color: TEXT_MUTED }}>
                                <strong style={{ color: TEXT_PRIMARY }}>{label}</strong> -- {desc}
                            </Typography>
                        </Box>
                    ))}
                    <TipBox>
                        Expenses are separate from activities. You can log an expense without it appearing on the itinerary -- e.g., food, souvenirs, or local transport.
                    </TipBox>
                </HelpSection>

                {/* ── 8. Sharing ────────────────────────────────────── */}
                <Typography variant="overline" id="sharing" sx={{ color: ACCENT, letterSpacing: 2, mb: 1, display: 'block', mt: 3 }}>
                    Collaboration
                </Typography>

                <HelpSection icon={GroupIcon} iconColor={ACCENT2} title="Sharing Trips">
                    <Typography variant="body2" style={{ color: TEXT_MUTED }} gutterBottom>
                        Invite other Agent Trip Planner users to collaborate on your trip. Shared users can view and edit the itinerary.
                    </Typography>

                    <Typography variant="subtitle2" fontWeight={700} style={{ color: TEXT_PRIMARY }} sx={{ mt: 1.5, mb: 1 }}>
                        How to share a trip
                    </Typography>
                    {[
                        'Open the trip itinerary.',
                        'Open the hamburger menu (top-left) and tap Share This Trip.',
                        "Enter the other user's User ID (shown in their side menu under their name).",
                        "They'll receive a share invitation in their Message Center.",
                        'Once accepted, both users see the same trip and can edit it.',
                    ].map((step, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 1 }}>
                            <StepBadge n={i + 1} />
                            <Typography variant="body2" style={{ color: TEXT_MUTED }}>{step}</Typography>
                        </Box>
                    ))}

                    <Divider sx={{ borderColor: DARK_BORDER, my: 2 }} />
                    <Typography variant="subtitle2" fontWeight={700} style={{ color: TEXT_PRIMARY }} gutterBottom>
                        Sharing page
                    </Typography>
                    <Typography variant="body2" style={{ color: TEXT_MUTED }}>
                        The <strong style={{ color: TEXT_PRIMARY }}>Sharing</strong> link in the side menu shows all trips shared with you and all trips you have shared with others.
                        From here you can accept, reject, or revoke access.
                    </Typography>
                </HelpSection>

                {/* ── 9. Memories ───────────────────────────────────── */}
                <HelpSection icon={PhotoLibraryIcon} iconColor={RED} title="Memories (Trip Photos)">
                    <Typography variant="body2" style={{ color: TEXT_MUTED }} gutterBottom>
                        Attach photos to any trip to build a visual diary of your journey.
                    </Typography>
                    {[
                        'Open a trip and tap 📸 Memories from the hamburger menu.',
                        'Tap Upload Photo to add photos from your device.',
                        'Each photo is stored with a caption and date.',
                        'Photos are visible to all users who share that trip.',
                    ].map((step, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 1 }}>
                            <StepBadge n={i + 1} />
                            <Typography variant="body2" style={{ color: TEXT_MUTED }}>{step}</Typography>
                        </Box>
                    ))}
                </HelpSection>

                {/* ── 10. Flight search ─────────────────────────────── */}
                <HelpSection icon={FlightIcon} iconColor="#29b6f6" title="Flight Search">
                    <Typography variant="body2" style={{ color: TEXT_MUTED }} gutterBottom>
                        Search for live flights directly within the app. Access via the side menu → <strong style={{ color: TEXT_PRIMARY }}>Flight Search</strong>.
                    </Typography>
                    {[
                        ['Origin & Destination', 'Type city or airport name  --  autocomplete suggests matching IATA codes.'],
                        ['Travel dates', 'Pick departure (and return for round-trip) using the date picker.'],
                        ['Passengers & class', 'Set number of adults, children, and preferred cabin class.'],
                        ['Results', 'Sorted by price by default. Tap a flight to see full segment details.'],
                        ['Add to itinerary', 'Use the Add button on any result to insert the flight directly into your trip.'],
                    ].map(([label, desc]) => (
                        <Box key={label} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <ArrowForwardIcon sx={{ fontSize: 14, color: '#29b6f6', mt: 0.5, flexShrink: 0 }} />
                            <Typography variant="body2" style={{ color: TEXT_MUTED }}>
                                <strong style={{ color: TEXT_PRIMARY }}>{label}</strong> -- {desc}
                            </Typography>
                        </Box>
                    ))}
                </HelpSection>

                {/* ── 11. Account ───────────────────────────────────── */}
                <HelpSection icon={ArchiveIcon} iconColor={TEXT_MUTED} title="Account, Archive & Settings">
                    <Typography variant="subtitle2" fontWeight={700} style={{ color: TEXT_PRIMARY }} gutterBottom>Profile Settings</Typography>
                    <Typography variant="body2" style={{ color: TEXT_MUTED }} gutterBottom>
                        Access via the hamburger menu → Profile Settings. You can update:
                    </Typography>
                    {['Display name', 'Linked email address', 'Password', 'WhatsApp number', 'Language preference'].map((item) => (
                        <Box key={item} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                            <ArrowForwardIcon sx={{ fontSize: 14, color: ACCENT, mt: 0.4, flexShrink: 0 }} />
                            <Typography variant="body2" style={{ color: TEXT_MUTED }}>{item}</Typography>
                        </Box>
                    ))}
                    <Divider sx={{ borderColor: DARK_BORDER, my: 2 }} />
                    <Typography variant="subtitle2" fontWeight={700} style={{ color: TEXT_PRIMARY }} gutterBottom>Archived Trips</Typography>
                    <Typography variant="body2" style={{ color: TEXT_MUTED }}>
                        Completed trips can be archived to keep the home screen tidy. Archived trips are still fully accessible -- nothing is deleted.
                        Restore any archived trip back to active at any time.
                    </Typography>
                </HelpSection>

                {/* ── Footer ──────────────────────────────────────────── */}
                <Box sx={{ textAlign: 'center', py: 4, mt: 2, borderTop: `1px solid ${DARK_BORDER}` }}>
                    <Typography variant="body2" style={{ color: TEXT_MUTED }}>
                        Questions? Contact us at{' '}
                        <a href="mailto:info@agentriplanner.com" style={{ color: ACCENT, fontWeight: 600, textDecoration: 'none' }}>
                            info@agentriplanner.com
                        </a>
                    </Typography>
                    <Typography variant="caption" style={{ color: TEXT_MUTED, display: 'block', mt: 0.5 }}>
                        Agent Trip Planner · AI-powered travel planning
                    </Typography>
                </Box>

            </Box>
        </Box>
    );
};

export default HelpPage;
